import React, { useCallback, useEffect, useRef, useState } from 'react';
import BEM from 'common/bem';
import getTigerAgentUrl from 'common/utils/getTigerAgentUrl';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';
import client from 'client';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';

const classes = BEM.with('TigerAgentPane');
const { setTigerAgentPaneOpen } = actions;

// Keep the iframe mounted through the pane's close animation before unmounting it.
// Must match $info-pane-transition-ms in styles/_vars.scss.
const PANE_TRANSITION_MS = 250;

const TIGER_AGENT_URL = getTigerAgentUrl(client.config.apiEnv);
const AGENT_ORIGIN = new URL(TIGER_AGENT_URL).origin;
const HOST_VERSION = buildInfo.version;
const SUPPORTED_EVENTS = ['bridge.handshake', 'auth.getCreds', 'ui.close', 'context.update'];

// Generic context.update payload ({ type, id, metadata }). metadata is string-only per the
// v1 contract.
type ContextUpdatePayload = {
  type: 'global' | 'org' | 'conversation';
  id: string;
  metadata: Record<string, string>;
};

// Only the fields the bridge reads off the MobX runtime models.
type AgentOrganization = { id: string; name?: string };
type AgentConversation = {
  id?: string;
  counterPartyName?: string;
  displayName?: string;
  featureService?: string;
  counterParty?: {
    patientDetails?: { mrn?: string };
    patientContextDetails?: { mrn?: string };
  };
};

// 1:1 patient conversations carry the mrn on patientDetails; group conversations with an
// attached patient context carry it on patientContextDetails instead.
function resolvePatientContextMrn(conversation: AgentConversation): string | undefined {
  return (
    conversation.counterParty?.patientDetails?.mrn ||
    conversation.counterParty?.patientContextDetails?.mrn ||
    undefined
  );
}

// Outbound envelopes the host sends into the SPA.
type OutboundEnvelope =
  | { v: 1; kind: 'event'; event: string; payload: ContextUpdatePayload }
  | { v: 1; kind: 'response'; id: string; ok: true; result: unknown }
  | { v: 1; kind: 'response'; id: string; ok: false; error: { code: string; message: string } };

// Inbound request envelope from the SPA.
type RequestEnvelope = { v: 1; kind: 'request'; id: string; event: string; payload?: unknown };

// Minimal typed surface for the SDK client's agentCore (the client type does not expose it).
type AgentCoreClient = { agentCore: { getOAuthToken(organizationId: string): Promise<string> } };

// Generic context.update payloads. Coerce every metadata value to a string.
export function buildOrgContext(org: AgentOrganization | null | undefined): ContextUpdatePayload {
  if (!org) return { type: 'global', id: '', metadata: {} };
  return { type: 'org', id: org.id ?? '', metadata: { name: org.name ?? '' } };
}

export function buildConversationContext(
  conversation: AgentConversation | null | undefined
): ContextUpdatePayload {
  if (!conversation) return { type: 'global', id: '', metadata: {} };
  const mrn = resolvePatientContextMrn(conversation);
  return {
    type: 'conversation',
    id: conversation.id ?? '',
    metadata: {
      counterPartyName: conversation.counterPartyName ?? '',
      displayName: conversation.displayName ?? '',
      featureService: conversation.featureService ?? '',
      ...(mrn ? { mrn } : {}),
    },
  };
}

// hostPlatform stays "web" even inside the Desktop App — a DA session is still a web session for
// clientApp purposes. hostDesktopVersion's presence (not hostPlatform) is what signals DA.
// Legacy .NET wrappers resolve their version asynchronously (see DesktopAppStore); until that
// resolves, desktopAppVersion is '' and hostDesktopVersion is omitted rather than sent empty or
// (worse) as an unresolved, non-cloneable function.
export function buildHandshakeReply(isDesktopApp: boolean, desktopAppVersion: string) {
  return {
    protocolVersion: 1,
    hostPlatform: 'web' as const,
    hostVersion: HOST_VERSION,
    ...(isDesktopApp && desktopAppVersion ? { hostDesktopVersion: desktopAppVersion } : {}),
    supportedEvents: SUPPORTED_EVENTS,
  };
}

// Decode a JWT's `exp` (seconds) into an ISO-8601 string, or undefined if it can't be read.
export function jwtExpiryIso(token: string): string | undefined {
  try {
    const segment = token.split('.')[1];
    if (!segment) return undefined;
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(base64));
    return typeof exp === 'number' ? new Date(exp * 1000).toISOString() : undefined;
  } catch {
    return undefined;
  }
}

function isRequestEnvelope(value: unknown): value is RequestEnvelope {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as { v?: unknown }).v === 1 &&
    (value as { kind?: unknown }).kind === 'request'
  );
}

type OwnProps = Record<string, never>;
type MobxProps = {
  currentConversation: AgentConversation | null;
  currentOrganization: AgentOrganization | null;
  currentUserId: string | null;
  isDesktopApp: boolean;
  desktopAppVersion: string;
};

export function TigerAgentPaneInner({
  currentConversation,
  currentOrganization,
  currentUserId,
  isDesktopApp,
  desktopAppVersion,
}: OwnProps & MobxProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const dispatch = useAppDispatch();
  const tigerAgentPaneOpen = useAppSelector(({ ui }) => ui.tigerAgentPaneOpen);

  // Mount the iframe (and boot the SPA) only once the pane is opened, and keep it mounted until
  // the close animation finishes so the agent fades out rather than vanishing instantly.
  const [shouldRenderIframe, setShouldRenderIframe] = useState<boolean>(tigerAgentPaneOpen);
  useEffect(() => {
    if (tigerAgentPaneOpen) {
      setShouldRenderIframe(true);
      return undefined;
    }
    const timer = setTimeout(() => setShouldRenderIframe(false), PANE_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [tigerAgentPaneOpen]);

  const postToIframe = useCallback((env: OutboundEnvelope) => {
    iframeRef.current?.contentWindow?.postMessage(env, AGENT_ORIGIN);
  }, []);

  const pushContext = useCallback(
    (payload: ContextUpdatePayload) => {
      postToIframe({ v: 1, kind: 'event', event: 'context.update', payload });
    },
    [postToIframe]
  );

  // Inbound: requests from the SPA.
  useEffect(() => {
    function replyOk(id: string, result: unknown) {
      postToIframe({ v: 1, kind: 'response', id, ok: true, result });
    }
    function replyError(id: string, code: string, message: string) {
      postToIframe({ v: 1, kind: 'response', id, ok: false, error: { code, message } });
    }

    async function handleMessage(ev: MessageEvent) {
      if (ev.source !== iframeRef.current?.contentWindow) return;
      if (ev.origin !== AGENT_ORIGIN) return;

      const env: unknown = ev.data;
      if (!isRequestEnvelope(env)) {
        if (env && typeof env === 'object' && typeof (env as { id?: unknown }).id === 'string') {
          replyError((env as { id: string }).id, 'bad_payload', 'invalid envelope');
        } else {
          // No id to correlate with — log and drop. The SPA is the bug.
          // eslint-disable-next-line no-console
          console.error('[tiger-agent host] bad_payload: invalid envelope shape', { env });
        }
        return;
      }

      switch (env.event) {
        case 'bridge.handshake':
          replyOk(env.id, buildHandshakeReply(isDesktopApp, desktopAppVersion));
          // SPA listener is guaranteed up by the time we got this request, so seed it with the
          // current org and conversation context immediately (org first).
          pushContext(buildOrgContext(currentOrganization));
          pushContext(buildConversationContext(currentConversation));
          break;
        case 'auth.getCreds': {
          if (!currentOrganization) {
            replyError(env.id, 'host_error', 'no current organization');
            break;
          }
          try {
            // Reuse the SDK's key/secret → org-scoped OAuth exchange (cached per org).
            const { agentCore } = client as unknown as AgentCoreClient;
            const token = await agentCore.getOAuthToken(currentOrganization.id);
            replyOk(env.id, {
              token,
              userId: currentUserId ?? '',
              orgId: currentOrganization.id,
              expiresAt: jwtExpiryIso(token),
            });
          } catch (e) {
            replyError(env.id, 'host_error', e instanceof Error ? e.message : String(e));
          }
          break;
        }
        case 'ui.close':
          dispatch(setTigerAgentPaneOpen(false));
          replyOk(env.id, null);
          break;
        default:
          replyError(env.id, 'unsupported_event', env.event);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    dispatch,
    postToIframe,
    pushContext,
    currentConversation,
    currentOrganization,
    currentUserId,
    isDesktopApp,
    desktopAppVersion,
  ]);

  // Outbound: push context.update when the conversation or org changes. Missed if the SPA hasn't
  // booted yet — handshake seeds the initial values above.
  useEffect(() => {
    pushContext(buildConversationContext(currentConversation));
  }, [currentConversation, pushContext]);

  useEffect(() => {
    if (currentOrganization) pushContext(buildOrgContext(currentOrganization));
  }, [currentOrganization, pushContext]);

  if (!shouldRenderIframe) return null;

  return (
    <iframe ref={iframeRef} src={TIGER_AGENT_URL} title="Ask Tiger" className={classes('iframe')} />
  );
}

export default mobxInjectSelect<OwnProps, MobxProps>({
  conversationStore: ['currentConversation'],
  messengerStore: ['currentOrganization'],
  sessionStore: ['currentUserId'],
  desktopAppStore: ['isDesktopApp', 'desktopAppVersion'],
})(TigerAgentPaneInner);
